import type { CompCriteria, CompSearchDefaults } from "./compCriteria";

/**
 * WIDEN CHIPS (C1, 2026-11-02). When fewer than `WIDEN_CHIPS_BELOW` sales pass the search, the panel
 * offers one chip per criterion that can be loosened by one step, and each chip says exactly what it
 * would change, from -> to. Pressing one is the agent's choice; nothing here widens on its own.
 *
 * The steps are the panel's own option lists (the server's ranges and band options from
 * `/api/next/comp-search/defaults`, the year list the panel already offers) — no new figure. A
 * criterion that is already at its widest has no chip; "Any living area" is never offered as a chip
 * because the panel marks it "not recommended" and that stays a deliberate choice in the options.
 *
 * Display-only: nothing here ranks, selects or prices a sale.
 */
export const WIDEN_CHIPS_BELOW = 6;
export const YEAR_STEPS = [3, 5, 10, 15, 20, 30];
/** One radius step: the same 0.5 mi the panel's default widen is made of. [ASSUMPTION] */
export const RADIUS_CHIP_STEP = 0.5;
/** One months step. [ASSUMPTION] half a year, the window agents most often reach for. */
export const MONTHS_CHIP_STEP = 6;

export interface WidenChip {
  key: string;
  /** "Radius" */
  what: string;
  from: string;
  to: string;
  next: CompCriteria;
}

const mi = (n: number) => `${Number(n.toFixed(2))} mi`;
const sq = (n: number) => `±${n.toLocaleString("en-US")} sq ft`;

function nextUp(options: readonly number[], cur: number): number | null {
  const up = [...options].sort((a, b) => a - b).find((o) => o > cur + 1e-9);
  return up ?? null;
}

export function widenChips(c: CompCriteria, defaults: CompSearchDefaults,
                           subject: { gla: number | null; lot?: number | null; year: number | null; subdivision: string | null }): WidenChip[] {
  const out: WidenChip[] = [];
  const r = defaults.ranges;
  const radiusTo = Math.min(r.radius_mi.max, Number((c.radius_mi + RADIUS_CHIP_STEP).toFixed(2)));
  if (radiusTo > c.radius_mi + 1e-9) {
    out.push({ key: "radius", what: "Radius", from: mi(c.radius_mi), to: mi(radiusTo), next: { ...c, radius_mi: radiusTo } });
  }
  const monthsTo = Math.min(r.months.max, c.months + MONTHS_CHIP_STEP);
  if (monthsTo > c.months) {
    out.push({ key: "months", what: "Sold within", from: `${c.months} months`, to: `${monthsTo} months`, next: { ...c, months: monthsTo } });
  }
  if (c.gla_band_sqft != null && subject.gla) {
    const to = nextUp(defaults.gla_band_sqft_options, c.gla_band_sqft);
    if (to !== null) out.push({ key: "gla", what: "Living area", from: sq(c.gla_band_sqft), to: sq(to), next: { ...c, gla_band_sqft: to, gla_band_pct: null } });
  }
  if (c.lot_band_pct != null && subject.lot) {
    const to = nextUp(defaults.lot_band_pct_options ?? [10, 15, 20, 25, 50], c.lot_band_pct);
    if (to !== null) out.push({ key: "lot", what: "Lot size", from: `±${c.lot_band_pct}%`, to: `±${to}%`, next: { ...c, lot_band_pct: to } });
  }
  if (c.year_band_years != null && subject.year) {
    const to = nextUp(YEAR_STEPS, c.year_band_years);
    out.push(to !== null
      ? { key: "year", what: "Year built", from: `within ${c.year_band_years} years`, to: `within ${to} years`, next: { ...c, year_band_years: to } }
      : { key: "year", what: "Year built", from: `within ${c.year_band_years} years`, to: "any year", next: { ...c, year_band_years: null } });
  }
  if (c.same_subdivision && subject.subdivision) {
    out.push({ key: "subdivision", what: "Subdivision", from: "same subdivision", to: "any subdivision", next: { ...c, same_subdivision: false } });
  }
  if (c.min_beds !== null) {
    out.push({ key: "beds", what: "Bedrooms", from: `${c.min_beds}+`, to: "any", next: { ...c, min_beds: null } });
  }
  if (c.min_baths !== null) {
    out.push({ key: "baths", what: "Bathrooms", from: `${c.min_baths}+`, to: "any", next: { ...c, min_baths: null } });
  }
  return out;
}

/** The sentence a pressed chip leaves behind: "Radius widened from 0.5 mi to 1 mi." */
export function widenedSentence(chip: Pick<WidenChip, "what" | "from" | "to">): string {
  return `${chip.what} widened from ${chip.from} to ${chip.to}.`;
}
